import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppData } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import FolderTabs from '../../components/common/FolderTabs';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import StoresMainPanel from '../../components/stores/StoresMainPanel';
import StoreImagesPanel from '../../components/stores/StoreImagesPanel';
import RolesPanel from '../../components/roles/RolesPanel';
import WaitlistQueuePanel from '../../components/waitlist/WaitlistQueuePanel';
import StoreAuthorizationHoursPanel from '../../components/stores/StoreAuthorizationHoursPanel';
import StoreTimePricingPanel from '../../components/stores/StoreTimePricingPanel';
import StoreBrandingPanel from '../../components/stores/StoreBrandingPanel';
import StoreCreationBlockedAlert from '../../components/stores/StoreCreationBlockedAlert';
import StoreService from '../../services/storeService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import usePermissions from '../../hooks/usePermissions';
import useStorePlan from '../../hooks/useStorePlan';
import { Permissions } from '../../constants/Permissions';
import { buildPublicStoreBookingUrl } from '../../utils/storeUtils';
import { STORE_HUB_TABS, resolveStoreHubTab } from './storeHubTabs';

const StoresPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentStore, storesLoading, storesError, refreshStores } = useAppData();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const storeId = currentStore?.id;
  const { hasPermission, isOwner, loading: permissionsLoading } = usePermissions(storeId);
  const { entitlements, isPro } = useStorePlan(storeId, Boolean(storeId));
  const allowsRoles = isPro && entitlements?.allowsRoles !== false;
  const allowsWaitlist = isPro && entitlements?.allowsWaitlist !== false;
  const allowsAdditionalHours = isPro && entitlements?.allowsAdditionalHours !== false;
  const allowsExtraStores = isPro && entitlements?.allowsExtraStores !== false;
  const allowsCustomBranding = isPro && entitlements?.allowsCustomBranding !== false;
  const lockProFeatures = Boolean(entitlements) && !isPro;

  const [store, setStore] = useState(null);
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeError, setStoreError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [creationEligibility, setCreationEligibility] = useState(null);

  const userType = user?.userType || user?.user_type;
  const isAppAdmin = userType === 1 || userType === '1' || userType === 'AppAdmin' || userType === 'appAdmin';
  const canCreateStore = isAppAdmin || hasPermission(Permissions.StoresCreate);
  const canUpdate = hasPermission(Permissions.StoresUpdate);
  const canViewImages = hasPermission(Permissions.StoresRead)
    || hasPermission(Permissions.StoresImagesAdd)
    || hasPermission(Permissions.StoresImagesDelete);
  const canViewRoles = hasPermission(Permissions.StoresRolesRead);
  const canViewWaitlist = hasPermission(Permissions.AppointmentsCreate) || isOwner;
  const canViewBranding = canUpdate
    || canViewImages
    || hasPermission(Permissions.StoresRead);

  const activeTab = resolveStoreHubTab(searchParams.get('tab'), {
    canViewImages,
    canViewRoles,
    canUpdate,
    canViewWaitlist,
    canViewBranding,
    allowsRoles,
    allowsWaitlist,
    allowsAdditionalHours,
  });

  const tabs = useMemo(() => ([
    { value: STORE_HUB_TABS.MAIN, label: t('stores.tabs.main') },
    ...(canViewImages ? [{ value: STORE_HUB_TABS.IMAGES, label: t('stores.tabs.images') }] : []),
    ...(canViewRoles ? [{
      value: STORE_HUB_TABS.ROLES,
      label: t('stores.tabs.roles'),
      mobileLabel: t('stores.tabs.rolesShort'),
      proLocked: lockProFeatures,
    }] : []),
    ...(canUpdate ? [{
      value: STORE_HUB_TABS.ON_REQUEST_HOURS,
      label: t('stores.tabs.onRequestHours'),
      mobileLabel: t('stores.tabs.onRequestHoursShort'),
      proLocked: lockProFeatures,
    }] : []),
    ...(canUpdate ? [{
      value: STORE_HUB_TABS.ADDITIONAL_COSTS,
      label: t('stores.tabs.additionalCosts'),
      mobileLabel: t('stores.tabs.additionalCostsShort'),
      proLocked: lockProFeatures,
    }] : []),
    ...(canViewWaitlist ? [{
      value: STORE_HUB_TABS.WAITLIST,
      label: t('stores.tabs.waitlist'),
      proLocked: lockProFeatures,
    }] : []),
    ...(canViewBranding ? [{ value: STORE_HUB_TABS.BRANDING, label: t('stores.tabs.branding') }] : []),
  ]), [canUpdate, canViewBranding, canViewImages, canViewRoles, canViewWaitlist, lockProFeatures, t]);

  const loadStore = useCallback(async () => {
    if (!storeId) {
      setStore(null);
      setStoreError(null);
      setStoreLoading(false);
      return;
    }

    return requestDeduplicator.deduplicate(`getStore-${storeId}`, async () => {
      try {
        setStoreLoading(true);
        setStoreError(null);
        const storeData = await StoreService.getStore(storeId);
        setStore(storeData);
      } catch (err) {
        setStoreError(err.message || t('stores.error.loading'));
        console.error('Error loading store:', err);
        throw err;
      } finally {
        setStoreLoading(false);
      }
    });
  }, [storeId, t]);

  useEffect(() => {
    loadStore();
  }, [loadStore]);

  const refreshStoreData = useCallback(async () => {
    await loadStore();
    await refreshStores();
  }, [loadStore, refreshStores]);

  useEffect(() => {
    if (isAppAdmin || !canCreateStore) {
      return undefined;
    }

    let cancelled = false;
    StoreService.getStoreCreationEligibility()
      .then((eligibility) => {
        if (!cancelled) setCreationEligibility(eligibility);
      })
      .catch((error) => {
        console.error('Failed to load store creation eligibility:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [isAppAdmin, canCreateStore]);

  const extraStoreBlocked = Boolean(entitlements) && !allowsExtraStores;
  const isCreationBlocked = !isAppAdmin && (
    (creationEligibility && !creationEligibility.canCreate)
    || extraStoreBlocked
  );
  const billingSetupPath = extraStoreBlocked && storeId
    ? `/management/stores/${storeId}/payment-settings`
    : (creationEligibility?.billingSetupStoreId
      ? `/management/stores/${creationEligibility.billingSetupStoreId}/payment-settings`
      : null);
  const creationBlockTitleKey = extraStoreBlocked ? 'storePlan.extraStoreTitle' : 'stores.creationBlockedTitle';
  const creationBlockMessageKey = extraStoreBlocked ? 'storePlan.extraStoreMessage' : 'stores.creationBlockedMessage';
  const creationBlockActionKey = extraStoreBlocked ? 'storePlan.upgradeCta' : 'stores.creationBlockedAction';

  const handleTabChange = (nextTab) => {
    setSearchParams(nextTab === STORE_HUB_TABS.MAIN ? {} : { tab: nextTab }, { replace: true });
  };

  const handleCopyBookingLink = async () => {
    const url = buildPublicStoreBookingUrl({
      storeId,
      slug: store?.slug || currentStore?.slug,
    });

    if (!url) {
      showError(t('stores.error.bookingLinkUnavailable'));
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      showSuccess(t('stores.feedback.bookingLinkCopied'));
    } catch (error) {
      console.error('Failed to copy booking link:', error);
      showError(t('stores.error.copyBookingLink'));
    }
  };

  const handleToggleStatus = async () => {
    if (!storeId || !store) return;
    try {
      setActionLoading(true);
      await StoreService.updateStoreStatus(storeId, !store.disabled);
      await Promise.all([loadStore(), refreshStores()]);
    } catch (err) {
      console.error('Error updating store status:', err);
      showError(err?.response?.data?.message || t('stores.error.updateStatus'));
    } finally {
      setActionLoading(false);
    }
  };

  if (storesError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('stores.error.loading')}: {storesError}
        </Alert>
        <Button onClick={() => refreshStores()} startIcon={<RefreshIcon />}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  if (storesLoading && !currentStore) {
    return <ManagementPageSkeleton variant="detail" />;
  }

  if (!currentStore) {
    const registerPath = isCreationBlocked
      ? billingSetupPath || '/management/stores/register'
      : '/management/stores/register';

    return (
      <ManagementPageLayout
        id="stores-page"
        pageTitle={t('stores.title')}
        pageSubtitle={t('stores.subtitle')}
      >
        <ManagementPageCard>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {canCreateStore && isCreationBlocked && (
            <Box sx={{ mb: 2 }}>
              <StoreCreationBlockedAlert
                billingSetupPath={billingSetupPath}
                titleKey={creationBlockTitleKey}
                messageKey={creationBlockMessageKey}
                actionKey={creationBlockActionKey}
              />
            </Box>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>{t('stores.emptySelected')}</Alert>
          {canCreateStore && (
            <PrimaryActionButton
              component={Link}
              to={registerPath}
              startIcon={<AddIcon />}
              disabled={isCreationBlocked && !billingSetupPath}
            >
              {isCreationBlocked ? t(creationBlockActionKey) : t('stores.addNew')}
            </PrimaryActionButton>
          )}
        </Box>
        </ManagementPageCard>
      </ManagementPageLayout>
    );
  }

  return (
    <ManagementPageLayout
      id="stores-page"
      containerId="stores-header"
      titleId="stores-title"
      subtitleId="stores-subtitle"
      pageTitle={currentStore.name || t('stores.title')}
      pageSubtitle={t('stores.subtitle')}
      contentId="stores-main-content"
    >
      <FolderTabs
        tabs={tabs}
        value={activeTab}
        onChange={handleTabChange}
        contentId="stores-tabs-content"
        equalWidthOnMobile
      >
        <Box role="tabpanel" hidden={activeTab !== STORE_HUB_TABS.MAIN} sx={{ minWidth: 0 }}>
          {activeTab === STORE_HUB_TABS.MAIN ? (
            <StoresMainPanel
              store={store}
              loading={storeLoading || permissionsLoading}
              error={storeError}
              canUpdate={canUpdate}
              canCreateStore={canCreateStore}
              isCreationBlocked={isCreationBlocked}
              addStoreProLocked={extraStoreBlocked}
              billingSetupPath={billingSetupPath}
              creationBlockTitleKey={creationBlockTitleKey}
              creationBlockMessageKey={creationBlockMessageKey}
              creationBlockActionKey={creationBlockActionKey}
              actionLoading={actionLoading}
              onRetry={loadStore}
              onEdit={() => navigate(`/management/stores/${storeId}/edit`)}
              onToggleStatus={handleToggleStatus}
              onCopyBookingLink={handleCopyBookingLink}
            />
          ) : null}
        </Box>
        {canViewImages && (
          <Box role="tabpanel" hidden={activeTab !== STORE_HUB_TABS.IMAGES}>
            {activeTab === STORE_HUB_TABS.IMAGES ? (
              <StoreImagesPanel storeId={storeId} store={store} onStoreChanged={loadStore} />
            ) : null}
          </Box>
        )}
        {canViewRoles && (
          <Box role="tabpanel" hidden={activeTab !== STORE_HUB_TABS.ROLES}>
            {activeTab === STORE_HUB_TABS.ROLES ? <RolesPanel storeId={storeId} /> : null}
          </Box>
        )}
        {canUpdate && allowsAdditionalHours && (
          <Box role="tabpanel" hidden={activeTab !== STORE_HUB_TABS.ON_REQUEST_HOURS} sx={{ minWidth: 0 }}>
            {activeTab === STORE_HUB_TABS.ON_REQUEST_HOURS ? (
              <StoreAuthorizationHoursPanel
                storeId={storeId}
                store={store}
                canUpdate={canUpdate}
                onSaved={(authorizationHours) => setStore((current) => (
                  current ? { ...current, authorizationHours } : current
                ))}
              />
            ) : null}
          </Box>
        )}
        {canUpdate && allowsAdditionalHours && (
          <Box role="tabpanel" hidden={activeTab !== STORE_HUB_TABS.ADDITIONAL_COSTS} sx={{ minWidth: 0 }}>
            {activeTab === STORE_HUB_TABS.ADDITIONAL_COSTS ? (
              <StoreTimePricingPanel
                storeId={storeId}
                store={store}
                canUpdate={canUpdate}
                hideProfessionalShare={!isPro || entitlements?.allowsProfessionalShare === false}
                onSaved={(timePricing) => setStore((current) => (
                  current ? { ...current, timePricing } : current
                ))}
              />
            ) : null}
          </Box>
        )}
        {canViewWaitlist && (
          <Box role="tabpanel" hidden={activeTab !== STORE_HUB_TABS.WAITLIST}>
            {activeTab === STORE_HUB_TABS.WAITLIST ? (
              <WaitlistQueuePanel storeId={storeId} storeDetails={store} />
            ) : null}
          </Box>
        )}
        {canViewBranding && (
          <Box role="tabpanel" hidden={activeTab !== STORE_HUB_TABS.BRANDING} sx={{ minWidth: 0 }}>
            {activeTab === STORE_HUB_TABS.BRANDING ? (
              <StoreBrandingPanel
                storeId={storeId}
                store={store}
                canUpdate={canUpdate}
                allowsCustomBranding={allowsCustomBranding}
                isOwner={isOwner}
                onStoreChanged={refreshStoreData}
                onSaved={(brandColors) => {
                  setStore((current) => (current ? { ...current, brandColors } : current));
                  refreshStores();
                }}
              />
            ) : null}
          </Box>
        )}
      </FolderTabs>
    </ManagementPageLayout>
  );
};

export default StoresPage;
export { resolveStoreHubTab } from './storeHubTabs';
